import React, { useCallback, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  useColorModeValue,
  Badge,
  Flex,
  useToast,
  Image,
} from "@chakra-ui/react";
import { MdClose, MdFileUpload, MdSchedule, MdImage } from "react-icons/md";

interface FileUploadProps {
  pendingFiles: File[];
  isUploadingFile: boolean;
  onFileUpload: (file: File) => Promise<void>;
  onRemoveFile: (fileId: string) => void;
  disabled?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  pendingFiles,
  isUploadingFile,
  onFileUpload,
  onRemoveFile,
  disabled = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const dragOverBg = useColorModeValue("blue.50", "blue.900");
  const dragOverBorder = useColorModeValue("blue.300", "blue.500");
  const pendingBg = useColorModeValue("orange.50", "orange.900");

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileUpload = useCallback(
    async (file: File) => {
      try {
        if (file.size > 10 * 1024 * 1024) {
          toast({
            title: "File too large",
            description: "File size must be less than 10MB",
            status: "error",
            duration: 3000,
          });
          return;
        }

        const allowedTypes = [
          "text/plain",
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/rtf",
          "application/vnd.ms-powerpoint",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "text/csv",
          "text/tab-separated-values",
          "text/x-c",
          "text/x-c++",
          "text/x-python",
          "text/x-java",
          "text/x-php",
          "text/x-sql",
          "text/html",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ];

        const allowedExtensions = [
          ".txt",
          ".pdf",
          ".doc",
          ".docx",
          ".rtf",
          ".dot",
          ".dotx",
          ".hwp",
          ".hwpx",
          ".pptx",
          ".xls",
          ".xlsx",
          ".csv",
          ".tsv",
          ".c",
          ".cpp",
          ".py",
          ".java",
          ".php",
          ".sql",
          ".html",
          ".jpg",
          ".jpeg",
          ".png",
        ];
        const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

        if (
          !allowedTypes.includes(file.type) &&
          !allowedExtensions.includes(fileExtension)
        ) {
          toast({
            title: "Unsupported file type",
            description:
              "Supported: Documents (PDF, DOC, DOCX, RTF, PPTX), Data (CSV, TSV, XLS, XLSX), Images (JPG, JPEG, PNG)",
            status: "error",
            duration: 3000,
          });
          return;
        }

        await onFileUpload(file);
      } catch (error) {
        toast({
          title: "Upload failed",
          description: "Failed to add file. Please try again.",
          status: "error",
          duration: 3000,
        });
      }
    },
    [toast, onFileUpload]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      const file = files[0];
      await handleFileUpload(file);
    },
    [disabled, handleFileUpload]
  );

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0]);
    }
    e.target.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isImageFile = (file: File) => {
    return (
      file.type.startsWith("image/") ||
      [".jpg", ".jpeg", ".png"].includes(
        "." + file.name.split(".").pop()?.toLowerCase()
      )
    );
  };

  const getFilePreviewURL = (file: File) => {
    if (isImageFile(file)) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  return (
    <VStack spacing={3} align="stretch">
      {pendingFiles.length > 0 && (
        <VStack spacing={1} align="stretch">
          <Text fontSize="xs" fontWeight="medium" color="orange.500">
            Pending files (will upload on send):
          </Text>
          <VStack spacing={1} align="stretch" maxH="100px" overflowY="auto">
            {pendingFiles.map((file, index) => {
              const previewURL = getFilePreviewURL(file);
              return (
                <Box
                  key={`pending-${index}`}
                  p={2}
                  bg={pendingBg}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="orange.200"
                >
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2} flex={1}>
                      {previewURL ? (
                        <Image
                          src={previewURL}
                          alt={file.name}
                          boxSize="32px"
                          objectFit="cover"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="orange.300"
                        />
                      ) : (
                        <Box color="orange.500">
                          {isImageFile(file) ? (
                            <MdImage size={16} />
                          ) : (
                            <MdSchedule size={16} />
                          )}
                        </Box>
                      )}
                      <VStack spacing={0} align="start" flex={1}>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                          {file.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {formatFileSize(file.size)}
                          {isImageFile(file) && " • Image"}
                        </Text>
                      </VStack>
                      <Badge colorScheme="orange" size="sm" variant="subtle">
                        Pending
                      </Badge>
                    </HStack>

                    <IconButton
                      aria-label="Remove file"
                      icon={<MdClose />}
                      size="xs"
                      variant="ghost"
                      onClick={() => onRemoveFile(file.name)}
                      disabled={disabled}
                    />
                  </Flex>
                </Box>
              );
            })}
          </VStack>
        </VStack>
      )}

      <Box
        border="2px dashed"
        borderColor={isDragOver ? dragOverBorder : borderColor}
        bg={isDragOver ? dragOverBg : bgColor}
        borderRadius="md"
        p={3}
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled ? 0.6 : 1}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (!disabled) {
            document.getElementById("file-input")?.click();
          }
        }}
        transition="all 0.2s"
        _hover={
          !disabled
            ? {
                borderColor: dragOverBorder,
                bg: dragOverBg,
              }
            : {}
        }
      >
        <input
          id="file-input"
          type="file"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
          disabled={disabled}
          accept=".txt,.pdf,.doc,.docx,.rtf,.dot,.dotx,.hwp,.hwpx,.pptx,.xls,.xlsx,.csv,.tsv,.c,.cpp,.py,.java,.php,.sql,.html,.jpg,.jpeg,.png"
        />

        <VStack spacing={1}>
          <MdFileUpload size={20} />
          <Text fontSize="sm" textAlign="center">
            {isUploadingFile
              ? "Processing..."
              : "Drop files here or click to browse"}
          </Text>
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Files will be uploaded when you send your message
          </Text>
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Supports: Documents (PDF, DOC, DOCX, RTF, PPTX), Data (CSV, TSV,
            XLS, XLSX), Images (JPG, JPEG, PNG)
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
};

export default FileUpload;
